/**
 * TradingWebSocketHandler - Subscribes via /ws/trading: positions and orders.
 * Must be inside TradingContext, StreamsContext, WebSocketProvider.
 */
import { useContext, useEffect, useRef, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AuthContext } from '../contexts/AuthContext';
import { TradingContext } from '../contexts/TradingContext';
import { StreamsContext } from '../contexts/StreamsContext';
import { AppStateContext } from '../contexts/AppStateContext';
import { BannerContext } from '../contexts/BannerContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { subscribe as wsSubscribe, unsubscribe as wsUnsubscribe, clearRefCountOnDisconnect } from '../utils/wsSubscriptionManager';
import { isOrderStatusFilled, normalizeOrderStatus } from '../services/tradestationUtils';
import { isBrokerLinked } from '../utils/brokerConnection';
import {
  onStreamRecovered,
  reportStreamError,
  shouldDeferStreamErrorBanner,
} from '../utils/streamErrorBanner';
import { recordOrderStreamUpdate } from '../utils/orderLatencyRecorder';
import { filledQty as resolveFilledQty } from '../utils/canonicalFields';
import { formatBrokerQuantity, positiveBrokerQuantity } from '../utils/brokerNumericFormat';
import { resolveClosedPositionPnl } from '../utils/closedPositionPnl';
import {
  ORDER_NOTICE_QUIET_MS,
  shouldSuppressOrderRejectNotice,
  shouldToastOrderStatus,
} from '../utils/orderStatusNotice';
import { consumeReplaceCancelNotice, upsertStreamOrder } from '../utils/orderReplace';

export default function TradingWebSocketHandler() {
  const queryClient = useQueryClient();
  const { user } = useContext(AuthContext);
  const { addTradingListener, sendTrading, tradingReady } = useWebSocket();
  const {
    selectedAccount,
    positionsManager,
    positions,
    setOrders,
    refreshBalancesOnly,
  } = useContext(TradingContext);
  const { setCurrentOrders, setPositionsStreamHealthy, setOrdersStreamHealthy } = useContext(StreamsContext);
  const { selectedTicker } = useContext(AppStateContext);
  const { showMessage } = useContext(BannerContext);

  const isInitializingRef = useRef(false);
  const didInitialRefreshRef = useRef(false);
  const pastInitialSnapshotRef = useRef(false);
  const suppressOrderNoticesUntilRef = useRef(0);
  const positionsHealthyRef = useRef(true);
  const knownPositionKeysRef = useRef(new Set());
  const knownPositionQtyRef = useRef(new Map());
  const knownPositionKeyByIdRef = useRef(new Map());
  const knownPositionByIdRef = useRef(new Map());
  const balanceRefreshTimerRef = useRef(null);
  useEffect(() => () => {
    if (Array.isArray(balanceRefreshTimerRef.current)) {
      balanceRefreshTimerRef.current.forEach((id) => clearTimeout(id));
    }
  }, []);

  const accountId = selectedAccount?.AccountID;
  const paperTrading = !!selectedAccount?.isPaperTrading;
  const ctAccountId = selectedAccount?.ct_account_id;
  const connectionId =
    selectedAccount?.ct_connection_id || selectedAccount?.connection_id || null;
  const adapterType = selectedAccount?.adapter_type || null;

  // `user` is replaced wholesale on virtually any settings write elsewhere in the app
  // (AuthContext.updateUserSettings -> setUser(newObject)) — e.g. the auto-persist-appSettings
  // effect in AppStateContext fires when targetingModeEnabled flips off after placing an order.
  // Depending on `user` directly in the subscribe effect below would cause positions/orders to
  // unsubscribe+resubscribe on any such write, even though nothing about accountId/paperTrading
  // changed. Memoize on only the fields isBrokerLinked() actually reads.
  const brokerLinked = useMemo(
    () => isBrokerLinked(user),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally scoped to primitive fields, not `user` itself
    [user?.hasBrokerConnection, user?.hasTradeStationCredentials]
  );

  const streamAccountMatches = useCallback(
    (dataAccountId) => {
      if (!dataAccountId || !accountId) return true;
      const ref = String(dataAccountId);
      if (ref === String(accountId)) return true;
      if (ctAccountId && ref === String(ctAccountId)) return true;
      return false;
    },
    [accountId, ctAccountId]
  );
  const toPositionKey = useCallback(
    (positionLike) => {
      const accountRef = positionLike?.AccountID || accountId;
      const symbol = positionLike?.Symbol;
      if (!accountRef || !symbol) return null;
      return `${String(accountRef)}::${String(symbol)}`;
    },
    [accountId]
  );
  const tradingReadyRef = useRef(tradingReady);
  useEffect(() => {
    tradingReadyRef.current = tradingReady;
    if (!tradingReady) positionsHealthyRef.current = false;
  }, [tradingReady]);
  useEffect(() => {
    const next = new Set();
    const nextQty = new Map();
    const nextKeyById = new Map();
    const nextPosById = new Map();
    for (const pos of positions || []) {
      const key = pos?.PositionKey || toPositionKey(pos);
      if (!key) continue;
      next.add(key);
      const qty = parseFloat(pos?.Quantity);
      if (Number.isFinite(qty)) nextQty.set(key, Math.abs(qty));
      const rawId = pos?.SourcePositionID ?? pos?.PositionID;
      if (rawId != null) {
        const id = String(rawId);
        nextKeyById.set(id, key);
        nextPosById.set(id, pos);
      }
    }
    knownPositionKeysRef.current = next;
    knownPositionQtyRef.current = nextQty;
    knownPositionKeyByIdRef.current = nextKeyById;
    knownPositionByIdRef.current = nextPosById;
  }, [positions, toPositionKey]);

  // Orders + positions streams start as soon as the trading socket is ready.
  // REST still loads the initial table snapshot; CT streams are incremental-only.
  // Do not wait for positions REST — that previously delayed live events by a full CT RTT.
  useEffect(() => {
    if (!tradingReady || !accountId || !brokerLinked) return;
    const orderParams = { accountId, paperTrading };
    if (connectionId) orderParams.connectionId = connectionId;
    if (adapterType) orderParams.adapterType = adapterType;
    wsSubscribe(sendTrading, 'orders', orderParams);
    return () => {
      const params = { accountId, paperTrading };
      if (connectionId) params.connectionId = connectionId;
      if (adapterType) params.adapterType = adapterType;
      if (tradingReadyRef.current === false) {
        clearRefCountOnDisconnect('orders', params);
      } else {
        wsUnsubscribe(sendTrading, 'orders', params);
      }
    };
  }, [tradingReady, accountId, paperTrading, connectionId, adapterType, sendTrading, brokerLinked]);

  useEffect(() => {
    if (!tradingReady || !accountId || !brokerLinked) return;
    const posParams = { accountId, paperTrading };
    if (connectionId) posParams.connectionId = connectionId;
    if (adapterType) posParams.adapterType = adapterType;
    wsSubscribe(sendTrading, 'positions', posParams);
    return () => {
      const params = { accountId, paperTrading };
      if (connectionId) params.connectionId = connectionId;
      if (adapterType) params.adapterType = adapterType;
      if (tradingReadyRef.current === false) {
        clearRefCountOnDisconnect('positions', params);
      } else {
        wsUnsubscribe(sendTrading, 'positions', params);
      }
    };
  }, [tradingReady, accountId, paperTrading, connectionId, adapterType, sendTrading, brokerLinked]);

  const handlePositions = useCallback(
    (data) => {
      if (data?.LateJoin) {
        positionsHealthyRef.current = true;
        onStreamRecovered('positions');
        setPositionsStreamHealthy(true);
        return;
      }
      if (data?.StreamStatus === 'EndSnapshot') {
        positionsHealthyRef.current = true;
        onStreamRecovered('positions');
        setPositionsStreamHealthy(true);
        if (isInitializingRef.current) isInitializingRef.current = false;
        return;
      }
      if (data?.AccountID && !streamAccountMatches(data.AccountID)) return;
      const applyPositionUpdate = (positionEvent) => {
        if (!positionEvent) return false;
        const rawPositionId =
          positionEvent?.PositionID != null
            ? String(positionEvent.PositionID)
            : (positionEvent?.SourcePositionID != null ? String(positionEvent.SourcePositionID) : null);
        const priorPosition = rawPositionId ? knownPositionByIdRef.current.get(rawPositionId) : null;
        const hydratedEvent = priorPosition ? { ...priorPosition, ...positionEvent } : positionEvent;
        const positionKey = toPositionKey(hydratedEvent) || (rawPositionId ? knownPositionKeyByIdRef.current.get(rawPositionId) : null);
        const wasKnown = !!(positionKey && knownPositionKeysRef.current.has(positionKey));
        const previousKnownQty = positionKey ? knownPositionQtyRef.current.get(positionKey) : null;
        const qty = parseFloat(hydratedEvent?.Quantity);
        const isClosed = hydratedEvent?.Deleted || (Number.isFinite(qty) && Math.abs(qty) === 0);
        const isExplicitDeleteEvent = hydratedEvent?.Deleted === true;
        const isVerifiedQtyToZeroClose =
          Number.isFinite(qty) &&
          Math.abs(qty) === 0 &&
          wasKnown &&
          Number.isFinite(previousKnownQty) &&
          previousKnownQty > 0;
        if (isClosed) {
          // Only auto-open journal when a known in-session open position is actually closed.
          // This avoids false prompts from transient/partial stream updates.
          const shouldAutoOpenJournal =
            (isExplicitDeleteEvent && wasKnown) || isVerifiedQtyToZeroClose;
          if (!shouldAutoOpenJournal || isInitializingRef.current) {
            console.log('[TradingWebSocketHandler] Skipping auto-journal for closed position:', {
              positionEvent: hydratedEvent,
              rawPositionId,
              positionKey,
              wasKnown,
              previousKnownQty,
              qty,
              isExplicitDeleteEvent,
              isVerifiedQtyToZeroClose,
              shouldAutoOpenJournal,
              isInitializing: isInitializingRef.current,
              knownIds: Array.from(knownPositionKeyByIdRef.current.keys()),
              knownKeys: Array.from(knownPositionKeysRef.current),
            });
          }
          if (shouldAutoOpenJournal && !isInitializingRef.current) {
            const eventQty = parseFloat(hydratedEvent?.Quantity);
            const priorQty = parseFloat(priorPosition?.Quantity);
            const closedPnl = resolveClosedPositionPnl(priorPosition, hydratedEvent);
            const journalPosition = {
              ...(priorPosition || {}),
              ...hydratedEvent,
              Deleted: true,
              Quantity: Number.isFinite(eventQty) && Math.abs(eventQty) > 0
                ? hydratedEvent.Quantity
                : (Number.isFinite(priorQty) ? priorPosition.Quantity : hydratedEvent?.Quantity),
            };
            if (closedPnl != null) {
              journalPosition.RealizedPnL = closedPnl;
              journalPosition.UnrealizedProfitLoss = closedPnl;
            }
            console.log('[TradingWebSocketHandler] Dispatching position:deleted for auto-journal:', journalPosition);
            try {
              window.dispatchEvent(new CustomEvent('position:deleted', { detail: { position: journalPosition } }));
            } catch (_) {}
          }
          if (positionKey) {
            positionsManager.removePosition(positionKey);
          } else {
            positionsManager.removePosition(hydratedEvent);
          }
          if (positionKey) knownPositionKeysRef.current.delete(positionKey);
          if (positionKey) knownPositionQtyRef.current.delete(positionKey);
          if (rawPositionId) {
            knownPositionKeyByIdRef.current.delete(rawPositionId);
            knownPositionByIdRef.current.delete(rawPositionId);
          }
          if (!isInitializingRef.current) {
            // Realized P&L is on the balance snapshot, and the broker often books it
            // after the position stream says the trade is closed. Keep asking.
            refreshBalancesOnly?.();
            if (balanceRefreshTimerRef.current) {
              balanceRefreshTimerRef.current.forEach((id) => clearTimeout(id));
            }
            balanceRefreshTimerRef.current = [2000, 10000, 30000, 90000, 150000].map((delay) => (
              setTimeout(() => refreshBalancesOnly?.(), delay)
            ));
          }
          return true;
        }

        // Do not copy Quantity (or the rest of the prior row) onto this tick.
        // A price-only event after a close would otherwise look like the position
        // is still open and get written back into state.
        const streamPatch = { ...positionEvent };
        if (priorPosition) {
          if (streamPatch.Symbol == null || streamPatch.Symbol === '') streamPatch.Symbol = priorPosition.Symbol;
          if (streamPatch.AccountID == null || streamPatch.AccountID === '') streamPatch.AccountID = priorPosition.AccountID;
          if (streamPatch.PositionID == null && priorPosition.PositionID != null) {
            streamPatch.PositionID = priorPosition.PositionID;
          }
          if (streamPatch.SourcePositionID == null && priorPosition.SourcePositionID != null) {
            streamPatch.SourcePositionID = priorPosition.SourcePositionID;
          }
        }
        positionsManager.updatePosition(streamPatch);
        if (positionKey) {
          knownPositionKeysRef.current.add(positionKey);
          if (Number.isFinite(qty)) knownPositionQtyRef.current.set(positionKey, Math.abs(qty));
        }
        if (rawPositionId && positionKey) {
          knownPositionKeyByIdRef.current.set(rawPositionId, positionKey);
          knownPositionByIdRef.current.set(rawPositionId, { ...(knownPositionByIdRef.current.get(rawPositionId) || {}), ...hydratedEvent });
        }
        if (!isInitializingRef.current && !wasKnown) {
          refreshBalancesOnly?.();
        }
        return true;
      };
      if (data?.Positions && Array.isArray(data.Positions)) {
        let handledAny = false;
        for (const pos of data.Positions) {
          handledAny = applyPositionUpdate(pos) || handledAny;
        }
        // TradeStation Direct delivers the initial open-positions list as a single
        // `Positions` array (no separate EndSnapshot for this channel) — treat its
        // arrival as "no longer initializing" so a genuine close arriving right after
        // (but before the first Heartbeat) isn't mistaken for a stale/pre-session delta
        // and silently skipped for the auto-journal prompt. Cleared after processing
        // the batch so any (unexpected) Deleted entries in the snapshot itself still
        // go through the normal initializing guard.
        isInitializingRef.current = false;
        if (handledAny) {
          setPositionsStreamHealthy(true);
          positionsHealthyRef.current = true;
          onStreamRecovered('positions');
        }
      } else if (data?.PositionID || data?.SourcePositionID || toPositionKey(data)) {
        if (applyPositionUpdate(data)) {
          setPositionsStreamHealthy(true);
          positionsHealthyRef.current = true;
          onStreamRecovered('positions');
        }
      } else if (data?.Heartbeat) {
        positionsHealthyRef.current = true;
        onStreamRecovered('positions');
        setPositionsStreamHealthy(true);
        if (isInitializingRef.current && !didInitialRefreshRef.current) {
          didInitialRefreshRef.current = true;
          isInitializingRef.current = false;
          refreshBalancesOnly?.();
        }
      } else if (data?.Error) {
        positionsHealthyRef.current = false;
        setPositionsStreamHealthy(false);
        if (data.Message) {
          reportStreamError({
            streamKey: 'positions',
            message: data.Message,
            showMessage,
            defer: shouldDeferStreamErrorBanner(data, tradingReadyRef.current),
            isHealthy: () => positionsHealthyRef.current,
          });
        }
      }
    },
    [positionsManager, setPositionsStreamHealthy, refreshBalancesOnly, showMessage, streamAccountMatches, toPositionKey]
  );

  const armOrderNoticeQuietPeriod = useCallback(() => {
    suppressOrderNoticesUntilRef.current = Date.now() + ORDER_NOTICE_QUIET_MS;
  }, []);

  // Keep the quiet window open while a snapshot burst is still arriving.
  // The first order after a gap is allowed through; its timestamp still has to be fresh.
  const slideOrderNoticeQuietPeriod = useCallback(() => {
    if (Date.now() < suppressOrderNoticesUntilRef.current) {
      suppressOrderNoticesUntilRef.current = Date.now() + ORDER_NOTICE_QUIET_MS;
    }
  }, []);

  const notifyOrderStatusIfNeeded = useCallback(
    (order, previousStatus, isNewOrder) => {
      if (!pastInitialSnapshotRef.current || !showMessage) return;
      if (!shouldToastOrderStatus({
        order,
        now: Date.now(),
        suppressUntil: suppressOrderNoticesUntilRef.current,
      })) return;
      const st = order.Status;
      const prevN = normalizeOrderStatus(previousStatus);
      const curN = normalizeOrderStatus(st);
      if (!isNewOrder && prevN === curN) return;

      const symbol = order.Legs?.[0]?.Symbol || 'Unknown';
      const orderType = order.OrderType || 'Order';
      const quantity =
        positiveBrokerQuantity(resolveFilledQty(order)) ||
        positiveBrokerQuantity(order.Quantity) ||
        formatBrokerQuantity(order.Quantity) ||
        '';
      const wasFilled = isOrderStatusFilled(previousStatus);
      const nowFilled = isOrderStatusFilled(st);

      if (nowFilled && !wasFilled) {
        const fillPrice = order.FilledPrice || order.Legs?.[0]?.ExecutionPrice || order.LimitPrice || order.StopPrice;
        showMessage(
          `Order Filled: ${quantity} ${symbol} ${orderType}${fillPrice ? ` at $${fillPrice}` : ''}`,
          'success',
          true,
          'banner'
        );
        return;
      }
      if (curN === 'REJ' && prevN !== 'REJ') {
        // Place-order HTTP already toasted the same rejection — skip the stream duplicate.
        if (shouldSuppressOrderRejectNotice(order.OrderID)) return;
        showMessage(
          `Order Rejected: ${quantity} ${symbol} ${orderType} - ${order.RejectReason || 'Unknown'}`,
          'danger',
          true,
          'banner'
        );
        return;
      }
      if (curN === 'CAN' && ['ACK', 'DON', 'OPN'].includes(prevN)) {
        if (consumeReplaceCancelNotice(order.OrderID)) return;
        showMessage(`Order Cancelled: ${quantity} ${symbol} ${orderType}`, 'warning', true, 'banner');
      }
    },
    [showMessage]
  );

  const invalidateOrderEventsForSymbol = useCallback((symbol) => {
    const sym = symbol ? String(symbol).trim().toUpperCase() : '';
    if (!sym) {
      queryClient.invalidateQueries({ queryKey: ['orderEvents'] });
      return;
    }
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === 'orderEvents' && key[1]?.symbol === sym;
      },
    });
  }, [queryClient]);

  const handleOrders = useCallback(
    (data) => {
      const activeOrderStatuses = new Set(['ACK', 'DON', 'OPN', 'PLA', 'WORKING', 'FPR', 'SUS']);
      const shouldKeepInCurrentOrders = (orderLike) => {
        if (!orderLike?.OrderID) return false;
        const status = normalizeOrderStatus(orderLike.Status);
        const symbol = orderLike?.Legs?.[0]?.Symbol || orderLike?.Symbol || null;
        if (symbol && selectedTicker && symbol !== selectedTicker) return false;
        if (!status) return true;
        return activeOrderStatuses.has(status);
      };

      if (data?.StreamStatus === 'EndSnapshot') {
        pastInitialSnapshotRef.current = true;
        armOrderNoticeQuietPeriod();
        setOrdersStreamHealthy(true);
        return;
      }
      if (data?.LateJoin) {
        pastInitialSnapshotRef.current = true;
        armOrderNoticeQuietPeriod();
        setOrdersStreamHealthy(true);
        return;
      }
      if (data?.AccountID && !streamAccountMatches(data.AccountID)) return;
      if (data?.Orders && Array.isArray(data.Orders)) {
        slideOrderNoticeQuietPeriod();
        if (pastInitialSnapshotRef.current) {
          data.Orders.forEach(recordOrderStreamUpdate);
        }
        const currentOrdersPatch = [];
        const retiredIds = [];
        const newlyFilledSymbols = new Set();
        setOrders((prev) => {
          const byId = new Map(prev.map((o) => [String(o.OrderID), o]));
          const notifyAfter = [];
          for (const ord of data.Orders) {
            if (!ord.OrderID) continue;
            if (ord.AccountID && !streamAccountMatches(ord.AccountID)) continue;
            const key = String(ord.OrderID);
            const old = byId.get(key);
            const merged = upsertStreamOrder(byId, ord);
            if (ord.ReplacedOrderID && String(ord.ReplacedOrderID) !== key) {
              retiredIds.push(String(ord.ReplacedOrderID));
            }
            currentOrdersPatch.push(merged);
            if (pastInitialSnapshotRef.current && isOrderStatusFilled(merged.Status) && (!old || !isOrderStatusFilled(old.Status))) {
              notifyAfter.push({ order: merged, previousStatus: old?.Status ?? null, isNew: !old });
              const sym = merged?.Legs?.[0]?.Symbol || merged?.Symbol;
              if (sym) newlyFilledSymbols.add(String(sym).toUpperCase());
            }
          }
          if (notifyAfter.length > 0) {
            queueMicrotask(() => {
              notifyAfter.forEach(({ order, previousStatus, isNew }) =>
                notifyOrderStatusIfNeeded(order, previousStatus, isNew)
              );
            });
          }
          return Array.from(byId.values());
        });
        if (newlyFilledSymbols.size > 0) {
          // Ledger write is async on the server; brief delay before refetching chart fills.
          setTimeout(() => {
            newlyFilledSymbols.forEach((sym) => invalidateOrderEventsForSymbol(sym));
          }, 1500);
        }
        if (currentOrdersPatch.length > 0) {
          setCurrentOrders((prev) => {
            const next = new Map(prev.map((o) => [String(o.OrderID), o]));
            for (const retiredId of retiredIds) next.delete(retiredId);
            for (const merged of currentOrdersPatch) {
              const key = String(merged.OrderID);
              if (shouldKeepInCurrentOrders(merged)) {
                next.set(key, { ...(next.get(key) || {}), ...merged });
              } else {
                next.delete(key);
              }
            }
            return Array.from(next.values());
          });
        }
        setOrdersStreamHealthy(true);
        return;
      }
      if (data?.OrderID) {
        if (data.AccountID && !streamAccountMatches(data.AccountID)) return;
        slideOrderNoticeQuietPeriod();
        if (pastInitialSnapshotRef.current) {
          recordOrderStreamUpdate(data);
        }
        let previousStatus = null;
        let isNewOrder = false;
        let mergedForNotify = data;
        let replacedId = null;
        let becameFilled = false;
        setOrders((prev) => {
          const byId = new Map(prev.map((o) => [String(o.OrderID), o]));
          const key = String(data.OrderID);
          const old = byId.get(key);
          previousStatus = old?.Status ?? null;
          isNewOrder = !old;
          mergedForNotify = upsertStreamOrder(byId, data);
          if (data.ReplacedOrderID && String(data.ReplacedOrderID) !== key) {
            replacedId = String(data.ReplacedOrderID);
          }
          becameFilled =
            pastInitialSnapshotRef.current &&
            isOrderStatusFilled(mergedForNotify.Status) &&
            (!old || !isOrderStatusFilled(old.Status));
          return Array.from(byId.values());
        });
        setOrdersStreamHealthy(true);
        if (pastInitialSnapshotRef.current) {
          notifyOrderStatusIfNeeded(mergedForNotify, previousStatus, isNewOrder);
        }
        if (becameFilled) {
          const sym = mergedForNotify?.Legs?.[0]?.Symbol || mergedForNotify?.Symbol;
          setTimeout(() => invalidateOrderEventsForSymbol(sym), 1500);
        }
        if (shouldKeepInCurrentOrders(mergedForNotify)) {
          setCurrentOrders((prev) => {
            const withoutRetired = replacedId
              ? prev.filter((o) => String(o.OrderID) !== replacedId)
              : prev;
            const idx = withoutRetired.findIndex((o) => String(o.OrderID) === String(data.OrderID));
            if (idx !== -1) {
              const updated = [...withoutRetired];
              updated[idx] = { ...withoutRetired[idx], ...mergedForNotify };
              return updated;
            }
            return [...withoutRetired, mergedForNotify];
          });
        } else {
          setCurrentOrders((prev) => prev.filter((o) => {
            const id = String(o.OrderID);
            return id !== String(data.OrderID) && id !== replacedId;
          }));
        }
      } else if (data?.Heartbeat) {
        setOrdersStreamHealthy(true);
      } else if (data?.Error) {
        setOrdersStreamHealthy(false);
      }
    },
    [
      selectedTicker,
      setOrders,
      setCurrentOrders,
      setOrdersStreamHealthy,
      notifyOrderStatusIfNeeded,
      streamAccountMatches,
      armOrderNoticeQuietPeriod,
      slideOrderNoticeQuietPeriod,
      invalidateOrderEventsForSymbol,
    ]
  );

  const handleMessage = useCallback(
    (msg) => {
      if (!msg || msg.error) return;
      const handlers = { positions: handlePositions, orders: handleOrders };
      const fn = handlers[msg.channel];
      if (fn && msg.data) fn(msg.data);
    },
    [handlePositions, handleOrders]
  );

  useEffect(() => {
    if (tradingReady) {
      isInitializingRef.current = true;
      didInitialRefreshRef.current = false;
      pastInitialSnapshotRef.current = false;
      suppressOrderNoticesUntilRef.current = 0;
    }
  }, [tradingReady, accountId]);

  useEffect(() => {
    if (!tradingReady) return;
    const remove = addTradingListener(handleMessage);
    return remove;
  }, [tradingReady, addTradingListener, handleMessage]);

  return null;
}
